import {
  Icon,
  IconBolt,
  IconBuilding,
  IconCalendarEvent,
  IconCrane,
  IconCup,
  IconFileSearch,
  IconHeartShare,
  IconHome,
  IconIdBadge2,
  IconListCheck,
  IconListNumbers,
  IconMessage2,
  IconMessageCircle2,
  IconNotebook,
  IconSearch,
  IconShieldCheck,
  IconUser,
} from '@tabler/icons-react'

import {
  openLiveChatToAnswers,
  openLiveChatToNewConversation,
} from '@/components/util-components/live-chat/LiveChat'
import { routes } from '@/utils/index'

export type CommandAction = {
  id: string
  name: string
  description?: React.ReactNode
  extra?: React.ReactNode
  icon?: Icon | React.ReactNode
  href?: string
  perform?: (closeCmdKModal: () => void) => void
  subActions?: Array<CommandAction | CommandGroup>
  filterResults?: boolean
  requiresProject?: boolean
}

export type CommandGroup = {
  id: string
  groupTitle: string
  groupActions: CommandAction[]
}

export type CommandType = CommandGroup | CommandAction

export type DynamicActionsProps = {
  activePage: string
  debouncedSearchText: string
  addSubItemsToParent: (parentId: string, subItems: Array<CommandType>) => void
  replaceParentSubItems: (
    parentId: string,
    subItems: Array<CommandType>
  ) => void
}

export enum CommandModule {
  Projects = 'projectsModule',
  Workers = 'workersModule',
  Subcontractors = 'subcontractorsModule',
  Checklists = 'checklistsModule',
  SiteInductions = 'siteInductionsModule',
  Tickets = 'ticketsModule',
  Registers = 'registersModule',
}

export const DEFAULT_CHECKLIST_ACTIONS: CommandType[] = [
  {
    id: 'goToChecklistList',
    name: 'Directory',
    href: routes.checklistsChecklists.checklistTypesList(),
  },
  {
    id: 'goToChecklistCreate',
    name: 'Create',
    href: routes.checklistsChecklists.newChecklist(),
  },
]

export const DEFAULT_SITE_INDUCTION_ACTIONS: CommandType[] = [
  {
    id: 'goToSiteInductionsList',
    name: 'Directory',
    href: routes.siteInductions.list(),
  },
  {
    id: 'goToSiteInductionsCreate',
    name: 'Create',
    href: routes.siteInductions.new(),
  },
]

export const DEFAULT_SUBCONTRACTOR_ACTIONS: CommandType[] = [
  {
    id: 'goToSubbieList',
    name: 'Directory',
    href: routes.subcontractors.list(),
  },
]

export const DEFAULT_TICKET_ACTIONS: CommandType[] = [
  {
    id: 'goToTicketList',
    name: 'Directory',
    href: routes.tickets.list(),
  },
]

export const DEFAULT_WORKER_ACTIONS: CommandType[] = [
  {
    id: 'goToWorkerList',
    name: 'Directory',
    href: routes.workers.list(),
  },
  {
    id: 'goToWorkerCreate',
    name: 'Create',
    href: routes.workers.new(),
  },
]

export const DEFAULT_REGISTER_ACTIONS: CommandType[] = [
  {
    id: 'goToRegistersList',
    name: 'Register Directory',
    href: routes.registers.list(),
  },
  {
    id: 'goToRegistersCreate',
    name: 'Create Register',
    href: routes.registers.new(),
  },
]

export const CMD_K_ITEMS: Array<CommandAction | CommandGroup> = [
  {
    id: 'modulesGroup',
    groupTitle: 'Modules',
    groupActions: [
      {
        id: 'goToDashboard',
        name: 'Home / Dashboards',
        icon: IconHome,
        subActions: [
          {
            id: 'goToProjectOverview',
            name: 'Home',
            href: routes.home(),
          },
          {
            id: 'goToProjectMap',
            name: 'Project Map',
            href: routes.projectMap(),
          },
          {
            id: 'goToImageGallery',
            name: 'Photos',
            href: routes.photos(),
          },
        ],
      },
      {
        id: CommandModule.Projects,
        name: 'Projects',
        icon: IconBuilding,
      },
      {
        id: CommandModule.Workers,
        name: 'Workers',
        icon: IconUser,
        filterResults: false,
        subActions: [
          ...DEFAULT_WORKER_ACTIONS,
          {
            id: 'searchForWorkers',
            name: 'Search Workers',
            icon: IconSearch,
            requiresProject: true,
          },
        ],
      },
      {
        id: CommandModule.Subcontractors,
        name: 'Companies',
        icon: IconCrane,
        filterResults: false,
        subActions: [
          ...DEFAULT_SUBCONTRACTOR_ACTIONS,
          {
            id: 'searchForSubbies',
            name: 'Search Companies',
            icon: IconSearch,
          },
        ],
      },
      {
        id: CommandModule.SiteInductions,
        name: 'siteInduction:plural',
        icon: IconShieldCheck,
        filterResults: false,
        subActions: [
          ...DEFAULT_SITE_INDUCTION_ACTIONS,
          {
            id: 'searchForInductions',
            name: 'siteInduction:labels.searchForInductions',
            icon: IconSearch,
            requiresProject: true,
          },
        ],
      },
      {
        id: CommandModule.Checklists,
        name: 'Forms',
        icon: IconListCheck,
        filterResults: false,
        subActions: [
          ...DEFAULT_CHECKLIST_ACTIONS,
          {
            id: 'searchForChecklists',
            name: 'Search Forms',
            icon: IconSearch,
          },
        ],
      },
      {
        id: 'goToSiteDiaries',
        name: 'siteDiary:plural',
        icon: IconNotebook,
        subActions: [
          {
            id: 'goToSiteDiariesList',
            name: 'Directory',
            href: routes.siteDiaries.list(),
          },
          {
            id: 'goToSiteDiariesCreate',
            name: 'Create',
            href: routes.siteDiaries.new(),
          },
        ],
      },
      {
        id: CommandModule.Tickets,
        name: 'ticket:plural',
        icon: IconIdBadge2,
        filterResults: false,
        subActions: [
          ...DEFAULT_TICKET_ACTIONS,
          {
            id: 'searchForTickets',
            name: 'ticket:titles.searchTickets',
            icon: IconSearch,
          },
        ],
      },
      {
        id: CommandModule.Registers,
        name: 'Registers',
        icon: IconListNumbers,
        filterResults: false,
        subActions: [
          ...DEFAULT_REGISTER_ACTIONS,
          {
            id: 'searchForRegisterItems',
            name: 'Search Register Items',
            icon: IconSearch,
          },
        ],
      },
      {
        id: 'goToSignOns',
        name: 'Sign Ons',
        icon: IconCalendarEvent,
        subActions: [
          {
            id: 'goToSignOnsList',
            name: 'Directory',
            href: routes.signOns.list(),
          },
          {
            id: 'goToSignOnsCreate',
            name: 'Create',
            href: routes.signOns.new(),
          },
        ],
      },
      {
        id: 'goToActions',
        name: 'Actions',
        icon: IconBolt,
        subActions: [
          {
            id: 'goToActionsList',
            name: 'Directory',
            href: routes.actions.list(),
          },
          {
            id: 'goToActionsCreate',
            name: 'Create',
            href: routes.actions.new(),
          },
        ],
      },
      {
        id: 'goToToolboxes',
        name: 'Toolboxes',
        icon: IconCup,
        subActions: [
          {
            id: 'goToToolboxesList',
            name: 'Directory',
            href: routes.toolboxes.list(),
          },
          {
            id: 'goToToolboxesCreate',
            name: 'Create',
            href: routes.toolboxes.new(),
          },
        ],
      },
      {
        id: 'goToCommunication',
        name: 'Communication',
        icon: IconMessage2,
        subActions: [
          {
            id: 'goToCommsList',
            name: 'Directory',
            href: routes.communication.list(),
          },
          {
            id: 'goToCommsCreate',
            name: 'Create',
            href: routes.communication.new(),
          },
        ],
      },
    ],
  },
  {
    id: 'help',
    groupTitle: 'Help',
    groupActions: [
      {
        id: 'liveChat',
        name: 'Contact Support',
        icon: IconMessageCircle2,
        perform: (close) => {
          openLiveChatToNewConversation()
          close()
        },
      },
      {
        id: 'knowledgeBase',
        name: 'Search Knowledge Base',
        icon: IconFileSearch,
        perform: (close) => {
          openLiveChatToAnswers()
          close()
        },
      },
    ],
  },
  {
    id: 'feedback',
    groupTitle: 'Share feedback',
    groupActions: [
      {
        id: 'feedback',
        name: 'Share feedback',
        icon: IconHeartShare,
        perform: () =>
          window.open(
            'https://portal.productboard.com/bglea2pbqglytesjttrbivvc/tabs/1-under-consideration/submit-idea'
          ),
      },
    ],
  },
]
